import {
  CheckCircle as CheckCircleIcon,
  Close as CloseIcon,
  Delete as DeleteIcon,
  MoreVert as MoreVertIcon,
  Star as StarIcon,
  StarBorder as StarBorderIcon,
  SupervisorAccount as SupervisorAccountIcon,
  Warning as WarningIcon,
} from "@mui/icons-material";
import {
  Avatar,
  Box,
  Card,
  CardContent,
  Chip,
  IconButton,
  ListItemIcon,
  ListItemText,
  Menu,
  MenuItem,
  Stack,
  Tooltip,
  Typography,
} from "@mui/material";
import { useState } from "react";

import { makeProfileAvatarUrl } from "~utils/url";

import { type MemberCardProps } from "./types";

// Centralized strings for internationalization
const tApproved = "Verified";
const tApproveTooltip = "Approve member";
const tDemoteFacilitator = "Demote Facilitator";
const tFacilitator = "Facilitator";
const tLeader = "Leader";
const tMoreActions = "More actions";
const tPendingApproval = "Not Verified";
const tPromoteFacilitator = "Promote Facilitator";
const tPromoteLeader = "Promote to Leader";
const tRejectTooltip = "Reject member";
const tRemoveMember = "Remove Member";

export const MemberCard = ({
  member,
  canManage,
  canSeeStatus,
  isOwner,
  onRemove,
  showPendingActions = false,
  onApprove,
  onReject,
  onAddFacilitator,
  onRemoveFacilitator,
  onAddOwner,
}: MemberCardProps) => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const menuOpen = Boolean(anchorEl);

  const displayName =
    member.firstName && member.lastName
      ? `${member.firstName} ${member.lastName}`
      : member.firstName || member.lastName || member.email || "Unknown User";

  const getInitials = (name: string) => {
    if (member.firstName && member.lastName) {
      return `${member.firstName[0]}${member.lastName[0]}`.toUpperCase();
    }
    return name.substring(0, 2).toUpperCase();
  };

  const handleMenuOpen = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleMenuAction = (action: () => void) => {
    action();
    handleMenuClose();
  };

  // Determine available actions based on permissions
  const showActionMenu = (canManage || isOwner) && !showPendingActions && !member.isOwner;
  const hasMenuItems = showActionMenu && (isOwner || (canManage && onRemove)); // Owner always has menu items available

  return (
    <Card
      sx={{
        "&:hover": {
          boxShadow: 4,
          transform: "translateY(-2px)",
        },
        borderLeft: "4px solid",
        borderLeftColor: member.isFacilitator ? "warning.main" : "primary.main",
        m: 1,
        transition: "all 0.2s ease",
        width: "100%",
      }}
    >
      <CardContent sx={{ "&:last-child": { pb: 2 }, p: 2 }}>
        <Stack direction="row" spacing={2} alignItems="flex-start">
          {/* Avatar */}
          <Avatar
            alt={displayName}
            src={makeProfileAvatarUrl(member.id)}
            sx={{
              backgroundColor: member.isFacilitator ? "warning.light" : "primary.light",
              color: member.isFacilitator ? "warning.main" : "primary.main",
              height: 40,
              width: 40,
            }}
          >
            {getInitials(displayName)}
          </Avatar>

          {/* Content */}
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Stack direction="row" alignItems="center" spacing={1}>
              <Typography
                variant="subtitle2"
                fontWeight="bold"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {displayName}
              </Typography>
              {member.isOwner && (
                <Chip
                  label={tLeader}
                  size="small"
                  sx={{
                    backgroundColor: "primary.main",
                    color: "primary.contrastText",
                    fontSize: "0.75rem",
                    height: "20px",
                  }}
                />
              )}
              {member.isFacilitator && !member.isOwner && (
                <Tooltip title={tFacilitator}>
                  <StarIcon sx={{ color: "warning.main", fontSize: "1rem" }} />
                </Tooltip>
              )}
            </Stack>

            {member.email && (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                  whiteSpace: "nowrap",
                }}
              >
                {member.email}
              </Typography>
            )}

            {/* Status Chips - Only visible to facilitators */}
            {canSeeStatus && (
              <Box sx={{ mt: 1 }}>
                <Stack direction="row" spacing={0.5} flexWrap="wrap">
                  {member.isVetted ? (
                    <Chip
                      icon={<CheckCircleIcon />}
                      label={tApproved}
                      size="small"
                      color="success"
                      variant="outlined"
                    />
                  ) : (
                    <Chip
                      icon={<WarningIcon />}
                      label={tPendingApproval}
                      size="small"
                      color="warning"
                      variant="outlined"
                    />
                  )}
                  {member.roles && member.roles.length > 0 && (
                    <Chip label={member.roles.join(", ")} size="small" variant="outlined" />
                  )}
                </Stack>
              </Box>
            )}
          </Box>

          {/* Actions */}
          <Stack direction="row" spacing={0.5}>
            {showPendingActions && canManage && (
              <>
                <Tooltip title={tApproveTooltip}>
                  <IconButton
                    size="small"
                    onClick={() => onApprove?.(member.id)}
                    sx={{ color: "success.main" }}
                    data-testid="approve-member-button"
                  >
                    <CheckCircleIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Tooltip title={tRejectTooltip}>
                  <IconButton
                    size="small"
                    onClick={() => onReject?.(member.id)}
                    sx={{ color: "error.main" }}
                    data-testid="reject-member-button"
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
              </>
            )}
            {hasMenuItems && (
              <>
                <Tooltip title={tMoreActions}>
                  <IconButton
                    size="small"
                    onClick={handleMenuOpen}
                    sx={{ color: "text.secondary" }}
                    data-testid="member-actions-button"
                  >
                    <MoreVertIcon fontSize="small" />
                  </IconButton>
                </Tooltip>
                <Menu
                  anchorEl={anchorEl}
                  open={menuOpen}
                  onClose={handleMenuClose}
                  anchorOrigin={{
                    horizontal: "right",
                    vertical: "bottom",
                  }}
                  transformOrigin={{
                    horizontal: "right",
                    vertical: "top",
                  }}
                >
                  {/* Owner-specific actions */}
                  {isOwner && (
                    <>
                      {!member.isFacilitator && (
                        <MenuItem onClick={() => handleMenuAction(() => onAddFacilitator?.(member.id))}>
                          <ListItemIcon>
                            <StarIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary={tPromoteFacilitator} />
                        </MenuItem>
                      )}
                      {member.isFacilitator && (
                        <MenuItem onClick={() => handleMenuAction(() => onRemoveFacilitator?.(member.id))}>
                          <ListItemIcon>
                            <StarBorderIcon fontSize="small" />
                          </ListItemIcon>
                          <ListItemText primary={tDemoteFacilitator} />
                        </MenuItem>
                      )}
                      <MenuItem onClick={() => handleMenuAction(() => onAddOwner?.(member.id))}>
                        <ListItemIcon>
                          <SupervisorAccountIcon fontSize="small" />
                        </ListItemIcon>
                        <ListItemText primary={tPromoteLeader} />
                      </MenuItem>
                    </>
                  )}
                  {/* Remove member action - available to both owners and facilitators */}
                  {onRemove && (
                    <MenuItem onClick={() => handleMenuAction(() => onRemove(member.id))}>
                      <ListItemIcon>
                        <DeleteIcon fontSize="small" />
                      </ListItemIcon>
                      <ListItemText primary={tRemoveMember} />
                    </MenuItem>
                  )}
                </Menu>
              </>
            )}
          </Stack>
        </Stack>
      </CardContent>
    </Card>
  );
};
